import { useState } from "react";
import {
  Menu,
  Search,
  Bell,
  Plus,
  ChevronDown,
} from "lucide-react";
import BrandLogo from "@/components/BrandLogo";
import { BRAND } from "@/brand";

/**
 * Reusable admin/shop shell — fixed dark sidebar + light top bar + main content.
 *
 * Props:
 *  - items: [{ key, label, icon: LucideIcon, badge?: string }]
 *  - activeKey / onNavigate
 *  - user: { name, email }
 *  - onLogout
 *  - businessLabel: string (e.g. "Field Ops Demo")
 *  - onCreate?: () => void  (top-right + Create button; hidden if not provided)
 *  - createLabel?: string (default "Create")
 *  - children: main content
 */
export default function DashboardShell({
  items,
  activeKey,
  onNavigate,
  user,
  onLogout,
  businessLabel,
  onCreate,
  createLabel = "Create",
  children,
}) {
  const [collapsed, setCollapsed] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showBiz, setShowBiz] = useState(false);
  const sidebarW = collapsed ? "w-20" : "w-64";
  const activeItem = items.find((it) => it.key === activeKey);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      {/* Sidebar */}
      <aside
        data-testid="dashboard-sidebar"
        className={`fixed inset-y-0 left-0 ${sidebarW} bg-slate-950 text-slate-200 flex flex-col z-40 transition-[width] duration-300 overflow-hidden`}
      >
        {/* Brand */}
        <div className="px-4 py-5 flex items-center gap-3 border-b border-white/5">
          <BrandLogo size={40} />
          {!collapsed && (
            <div className="min-w-0">
              <div className="font-bold text-white text-base leading-none truncate">{BRAND.companyName}</div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-sky-300/80 mt-1.5 font-semibold truncate">{BRAND.productName}</div>
            </div>
          )}
        </div>

        {/* Nav items */}
        <nav className="flex-1 overflow-y-auto py-4 space-y-1 px-3">
          {items.map((it) => {
            const Icon = it.icon;
            const isActive = activeKey === it.key;
            return (
              <button
                key={it.key}
                onClick={() => onNavigate(it.key)}
                data-testid={`nav-${it.key}`}
                title={collapsed ? it.label : undefined}
                className={`w-full inline-flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                  isActive
                    ? "bg-blue-600 text-white shadow-lg shadow-blue-600/30"
                    : "text-slate-300 hover:bg-white/5 hover:text-white"
                }`}
              >
                <Icon className="w-5 h-5 shrink-0" />
                {!collapsed && (
                  <>
                    <span className="flex-1 text-left truncate">{it.label}</span>
                    {it.badge && (
                      <span className={`px-1.5 py-0.5 text-[10px] font-bold rounded-md ${
                        isActive ? "bg-white/20 text-white" : "bg-amber-500/90 text-white"
                      }`}>{it.badge}</span>
                    )}
                  </>
                )}
              </button>
            );
          })}
        </nav>

        {/* User footer */}
        <div className="border-t border-white/5 p-3 relative">
          <button
            onClick={() => setShowUserMenu((v) => !v)}
            data-testid="user-menu-toggle"
            className="w-full inline-flex items-center gap-3 px-2 py-2 rounded-xl text-sm hover:bg-white/5 text-slate-200"
          >
            <div className="w-8 h-8 shrink-0 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-bold">
              {(user?.name || user?.email || "U").slice(0, 1).toUpperCase()}
            </div>
            {!collapsed && (
              <div className="flex-1 min-w-0 text-left">
                <div className="text-xs font-semibold text-white truncate">{user?.name || "Admin"}</div>
                <div className="text-[10px] text-slate-400 truncate">{user?.email}</div>
              </div>
            )}
          </button>
          {showUserMenu && (
            <div className="absolute bottom-16 left-3 right-3 bg-slate-900 border border-white/10 rounded-xl shadow-2xl overflow-hidden">
              <button
                onClick={onLogout}
                data-testid="user-menu-logout"
                className="w-full px-4 py-2.5 text-left text-sm text-rose-300 hover:bg-rose-500/10"
              >
                Sign out
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* Content area */}
      <div className={`${collapsed ? "ml-20" : "ml-64"} min-h-screen transition-[margin] duration-300`}>
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-white border-b border-slate-200 h-16 flex items-center gap-3 px-4 sm:px-6">
          <button
            onClick={() => setCollapsed((v) => !v)}
            data-testid="sidebar-toggle"
            className="p-2 rounded-lg hover:bg-slate-100 text-slate-600"
            aria-label="Toggle sidebar"
          >
            <Menu className="w-5 h-5" />
          </button>

          <button
            onClick={() => setShowBiz((v) => !v)}
            data-testid="business-selector"
            className="hidden sm:inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-200 hover:bg-slate-50 text-sm font-semibold text-slate-800"
          >
            <span className="w-6 h-6 rounded-md bg-blue-100 text-blue-700 text-[11px] font-bold flex items-center justify-center">
              {businessLabel?.slice(0, 1)?.toUpperCase() || "B"}
            </span>
            {businessLabel}
            <ChevronDown className="w-3.5 h-3.5 opacity-60" />
          </button>

          <div className="flex-1 max-w-lg relative hidden md:block">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              placeholder="Search anything…"
              data-testid="global-search"
              className="w-full rounded-xl border border-slate-200 bg-slate-50 pl-9 pr-4 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:bg-white"
            />
          </div>

          <div className="ml-auto flex items-center gap-2">
            {onCreate && (
              <button
                onClick={onCreate}
                data-testid="topbar-create"
                className="btn-primary rounded-xl px-4 py-2 text-sm font-semibold inline-flex items-center gap-1.5"
              >
                <Plus className="w-4 h-4" /> {createLabel}
              </button>
            )}
            <button
              data-testid="topbar-notifications"
              className="p-2 rounded-xl hover:bg-slate-100 text-slate-600 relative"
              aria-label="Notifications"
            >
              <Bell className="w-5 h-5" />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-rose-500" />
            </button>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 sm:p-6 lg:p-8">
          {activeItem && (
            <div className="mb-6">
              <div className="text-[11px] tracking-[0.25em] uppercase font-bold text-blue-700">{activeItem.eyebrow || "Overview"}</div>
              <h1 className="font-display text-3xl font-black text-slate-900 mt-1">{activeItem.title || activeItem.label}</h1>
              {activeItem.subtitle && <p className="text-sm text-slate-500 mt-1">{activeItem.subtitle}</p>}
            </div>
          )}
          {children}
        </main>
      </div>
    </div>
  );
}
